import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { type ColumnDef } from "@tanstack/react-table";
import { Plus, Pencil, Trash2, Calendar, Tag } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { DataTable } from "@/components/ui/DataTable";
import { Card, CardContent } from "@/components/ui/Card";
import { EventForm } from "./EventForm";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface EventRow {
  id: number;
  agency_id: number;
  name: string;
  event_date: string | null;
  category_id: number | null;
  category_name?: string;
  site: string | null;
  duration: number | null;
  attendee_count: number | null;
  demographics: string | null;
  notes: string | null;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function EventsList() {
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [editingEvent, setEditingEvent] = useState<EventRow | null>(null);
  const [showCategoryForm, setShowCategoryForm] = useState(false);
  const [categoryName, setCategoryName] = useState("");

  const { data: events, isLoading } = useQuery<EventRow[]>({
    queryKey: ["events"],
    queryFn: async () => {
      const res = await api.get<{ data: EventRow[] }>("/events");
      return res.data;
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => api.delete(`/events/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["events"] });
    },
  });

  const createCategoryMutation = useMutation({
    mutationFn: (data: { name: string }) =>
      api.post("/event-categories", data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["event-categories"] });
      setShowCategoryForm(false);
      setCategoryName("");
    },
  });

  function handleEdit(event: EventRow) {
    setEditingEvent(event);
    setShowForm(true);
  }

  function handleCloseForm() {
    setShowForm(false);
    setEditingEvent(null);
  }

  const columns: ColumnDef<EventRow, unknown>[] = [
    {
      accessorKey: "name",
      header: "Event Name",
      cell: ({ getValue }) => (
        <span className="font-medium">{getValue() as string}</span>
      ),
    },
    {
      accessorKey: "event_date",
      header: "Date",
      cell: ({ getValue }) => {
        const val = getValue() as string | null;
        return val ? new Date(val).toLocaleDateString() : "--";
      },
    },
    {
      accessorKey: "category_name",
      header: "Category",
      cell: ({ getValue }) => getValue() ?? "--",
    },
    {
      accessorKey: "site",
      header: "Site",
      cell: ({ getValue }) => getValue() ?? "--",
    },
    {
      accessorKey: "attendee_count",
      header: "Attendees",
      cell: ({ getValue }) => getValue() ?? "--",
    },
    {
      accessorKey: "duration",
      header: "Duration",
      cell: ({ getValue }) => {
        const val = getValue() as number | null;
        return val != null ? `${val}h` : "--";
      },
    },
    {
      id: "actions",
      header: "",
      cell: ({ row }) => (
        <div className="flex justify-end gap-1">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => handleEdit(row.original)}
          >
            <Pencil className="h-3.5 w-3.5" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => {
              if (confirm("Delete this event?")) {
                deleteMutation.mutate(row.original.id);
              }
            }}
          >
            <Trash2 className="h-3.5 w-3.5 text-destructive" />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-foreground">
            Community Events
          </h2>
          <p className="text-sm text-muted-foreground">
            Track community outreach events and attendance
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowCategoryForm(!showCategoryForm)}
          >
            <Tag className="mr-1.5 h-3.5 w-3.5" />
            Add Category
          </Button>
          <Button
            size="sm"
            className="gap-2"
            onClick={() => {
              handleCloseForm();
              setShowForm(true);
            }}
          >
            <Plus className="h-4 w-4" />
            New Event
          </Button>
        </div>
      </div>

      {showCategoryForm && (
        <Card>
          <CardContent className="p-4">
            <form
              onSubmit={(e) => {
                e.preventDefault();
                createCategoryMutation.mutate({ name: categoryName });
              }}
              className="flex items-end gap-3"
            >
              <div className="flex-1">
                <Input
                  label="Category Name"
                  value={categoryName}
                  onChange={(e) => setCategoryName(e.target.value)}
                  required
                  placeholder="e.g. Community Outreach, Training, Fundraiser"
                />
              </div>
              <Button
                type="submit"
                size="sm"
                loading={createCategoryMutation.isPending}
              >
                Add
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setShowCategoryForm(false)}
              >
                Cancel
              </Button>
            </form>
          </CardContent>
        </Card>
      )}

      {showForm && (
        <EventForm event={editingEvent} onClose={handleCloseForm} />
      )}

      {!isLoading && (events ?? []).length === 0 && !showForm && (
        <Card>
          <CardContent className="p-8 text-center">
            <Calendar className="mx-auto h-10 w-10 text-muted-foreground" />
            <p className="mt-2 text-sm text-muted-foreground">
              No events recorded yet.
            </p>
          </CardContent>
        </Card>
      )}

      <DataTable
        columns={columns}
        data={events ?? []}
        searchColumn="name"
        searchPlaceholder="Search events..."
        isLoading={isLoading}
      />
    </div>
  );
}
